import { forwardRef, type MouseEvent } from 'react';
import { FolderOpen } from 'lucide-react';

import { AccureIcon } from '@/components/ui/icons/accure';
import { Tooltip } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { cls } from '@/utils/cls';

import { TaskCardType } from '../../entities/task-card-type';
import type { TaskPreview } from '../../entities/task-preview';
import { TaskStatus } from '../../entities/task-status';
import type { DropData } from '../../types/drop-data';
import { AlertTypeBadge } from '../alert-type-badge';
import { TaskAlertStatusBadge } from '../task-alert-status-badge';
import { TaskPriorityBadge } from '../task-priority-badge';

import { ResolvedAlertTooltipContent } from './resolved-alert-tooltip-content';

export type TaskCardProps = {
  draggable?: boolean;
  task: TaskPreview;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
};

const TaskCard = forwardRef<HTMLDivElement, TaskCardProps>(
  ({ task, draggable, onClick }, ref) => {
    const featureFlag = useFeatureFlag();
    const projectManagementInfo =
      'projectManagement' in task ? task.projectManagement : undefined;

    const isResolved =
      task.cardType === TaskCardType.ALERT && task.alert?.resolved;

    return (
      <div
        ref={ref}
        draggable={draggable}
        onDragStart={function (e) {
          const drop: DropData = { type: 'move-task', task };
          e.dataTransfer.setData('text/plain', JSON.stringify(drop));
        }}
        onClick={onClick}
        className={cls(
          'flex flex-col gap-4 rounded border-2 border-slate-200/60 bg-white/60 p-4 shadow hover:border-slate-200 hover:bg-gray-50',
          {
            'cursor-pointer': draggable || onClick,
            'border-gray-300/60 bg-gray-100/60 opacity-60': isResolved,
          },
        )}
        data-testid="task-card"
        data-accure-card-type={task.cardType}
        data-accure-card-status={task.status}
      >
        <Tooltip
          content={<ResolvedAlertTooltipContent />}
          delay
          hide={!isResolved}
        >
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-baseline gap-4">
              <div
                className="grow truncate text-base font-normal leading-normal text-neutral-700"
                data-testid="task-card-title"
              >
                {task.title}
              </div>
              {task.status !== TaskStatus.DONE ? (
                <TaskPriorityBadge priority={task.priority} />
              ) : null}
            </div>
            {task.cardType === TaskCardType.ALERT &&
            task.alert?.suggestedAction ? (
              <div className="truncate text-base font-light leading-normal text-neutral-500">
                {task.alert.suggestedAction}
              </div>
            ) : (task.cardType === TaskCardType.STANDARD ||
                task.cardType === TaskCardType.DATA_ONBOARDING_INCIDENT) &&
              task.subtitle ? (
              <div className="truncate text-base font-light leading-normal text-neutral-500">
                {task.subtitle}
              </div>
            ) : null}
          </div>

          {task.status !== TaskStatus.DONE ? (
            task.cardType === TaskCardType.ALERT ? (
              <div className="flex flex-row gap-4">
                <div className="grow">
                  <AccureIcon className="size-6" />
                </div>
                {task.alert ? (
                  <div className="flex gap-2">
                    {task.alert.type ? (
                      <AlertTypeBadge type={task.alert.type} />
                    ) : null}
                    {task.alert.status ? (
                      <TaskAlertStatusBadge status={task.alert.status} />
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : task.cardType === TaskCardType.DATA_ONBOARDING_INCIDENT ? (
              <div className="flex flex-row gap-4">
                <div className="grow">
                  <AccureIcon className="size-6" />
                </div>
              </div>
            ) : null
          ) : null}

          {featureFlag('projectManagement') && projectManagementInfo ? (
            <div className="flex">
              <div
                className="flex gap-1 truncate rounded-full py-1 text-xs font-normal leading-none text-neutral-400 hover:text-neutral-500"
                data-testid="project-management-info"
              >
                <FolderOpen className="size-3 shrink-0" />
                {projectManagementInfo.workstream.title}
              </div>
            </div>
          ) : null}
        </Tooltip>
      </div>
    );
  },
);

TaskCard.displayName = 'TaskCard';
export { TaskCard };
